import { ReactNode, useRef, useEffect, Children, isValidElement } from 'react';
import OutsideClickWatcher from '@utility/OutsideClickWatcher/OutsideClickWatcher';
import { StyledChildWrapper, StyledPopoverMenu } from './PopoverMenu.styles';

interface PopoverMenuProps {
  open: boolean;
  children: ReactNode;
  handleClose: () => void;
  'data-testid'?: string;
  className?: string;
}

const PopoverMenu = ({ open, handleClose, children, className, ...props }: PopoverMenuProps) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (open) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (!menuRef.current) return;

        const focusableItems = itemRefs.current.filter(Boolean);
        const currentIndex = focusableItems.indexOf(document.activeElement as HTMLDivElement);

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const nextIndex = (currentIndex + 1) % focusableItems.length;
          focusableItems[nextIndex]?.focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          const prevIndex = (currentIndex - 1 + focusableItems.length) % focusableItems.length;
          focusableItems[prevIndex]?.focus();
        } else if (e.key === 'Escape') {
          handleClose();
        }
      };

      // focus on first element when menu opens
      itemRefs.current.find(Boolean)?.focus();

      menuRef.current?.addEventListener('keydown', handleKeyDown);
      return () => menuRef.current?.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, handleClose]);

  const childArray = Children.toArray(children);

  return (
    <>
      {open && (
        <OutsideClickWatcher clickHandler={handleClose}>
          <StyledPopoverMenu ref={menuRef} data-testid={props['data-testid']} className={className} role="menu">
            {childArray.map((child, index) => {
              if (isValidElement(child)) {
                const childOnClick = child.props.onClick;
                const childDisabled = child.props.disabled;
                return (
                  <StyledChildWrapper
                    key={index}
                    ref={(el) => {
                      itemRefs.current[index] = el;
                    }}
                    tabIndex={0}
                    role="menuitem"
                    onClick={(e) => {
                      if (childOnClick && !childDisabled) {
                        e.preventDefault();
                        childOnClick(e);
                        handleClose();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && childOnClick && !childDisabled) {
                        e.preventDefault();
                        childOnClick(e);
                        handleClose();
                      }
                    }}
                  >
                    {child}
                  </StyledChildWrapper>
                );
              }
              return null;
            })}
          </StyledPopoverMenu>
        </OutsideClickWatcher>
      )}
    </>
  );
};

export default PopoverMenu;
